import React from "react";

import { injectIntl, IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import Toilet from "@/icons/__generated__/ToiletPaper/ToiletPaper24";
import { IFaqs } from "~/types/api-types";
import { IRestroom } from "~/types/local-types";

import {
  eventFaqDefinition,
  subtitles,
  category_labels,
} from "../data/event-faq-definitions";
import {
  renderEntryTitle,
  restroomsSelected,
} from "../data/event-faq-display-sections";

import Description from "./Description";
import { analyticEventOnClick } from "./faq-utils";

interface RestroomsProps {
  faqData: IFaqs;
  intl: IntlShape;
  userID: string;
  listingID: string;
}

interface RestroomOptions {
  standard_restrooms: string;
  ada_restrooms: string;
  nursing_rooms: string;
}

const Restrooms: React.FC<RestroomsProps> = ({
  faqData,
  intl,
  userID,
  listingID,
}) => {
  const restroomCopy = eventFaqDefinition.restroom as IRestroom;
  const { selectedRestrooms, notSelectedRestrooms } = restroomsSelected(
    faqData,
  ) as {
    selectedRestrooms: (keyof RestroomOptions)[];
    notSelectedRestrooms: (keyof RestroomOptions)[];
  };

  const label = intl.formatMessage(category_labels.restrooms);

  const getRestroomMessage = (restroom: string) => {
    return restroomCopy[restroom as keyof RestroomOptions];
  };

  const getNonSelectedRestroomMessage = (restroom: string) => {
    return restroomCopy[restroom as keyof RestroomOptions];
  };

  return (
    <div className="EventFAQ">
      {faqData && (
        <Accordion
          type="multiple"
          onValueChange={() => {
            analyticEventOnClick(userID, listingID, "Restrooms");
          }}
        >
          <AccordionItem value="restroom-section">
            <AccordionTrigger className="tw-py-24">
              <div className="tw-flex tw-gap-12 tw-items-center">
                <Toilet />
                <Text element="h3" typeface="headline-1-m">
                  {label}
                </Text>
              </div>
            </AccordionTrigger>
            <AccordionContent className="tw-ml-36 tw-pb-24 tw-space-y-8">
              <Text typeface="body-2-m" element="p">
                {intl.formatMessage(subtitles.restrooms_and_facilities)}
              </Text>
              {selectedRestrooms.length > 0 && (
                <>
                  {selectedRestrooms.map((restroom, key) => (
                    <div key={key}>
                      {renderEntryTitle(
                        getRestroomMessage(restroom),
                        faqData[restroom]?.quantity,
                        restroom,
                      )}
                      {faqData[restroom]?.description && (
                        <Description
                          description={faqData[restroom].description}
                          textSmall
                        />
                      )}
                    </div>
                  ))}
                </>
              )}
              {notSelectedRestrooms.length > 0 && (
                <>
                  {notSelectedRestrooms.map((restroom, key) => (
                    <Text
                      element="p"
                      className="tw-text-gray-600 tw-mb-16 tw-line-through"
                      key={key}
                    >
                      {intl.formatMessage(
                        getNonSelectedRestroomMessage(restroom),
                      )}
                    </Text>
                  ))}
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export default injectIntl(Restrooms);
